/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Divider, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Button from 'components/common/Button';
import SelectWithSearch from 'components/common/SelectWithSearch';
import Backdrop from 'components/backdrop/Backdrop';
import { isFeatureEnabled, ToggleableFeature } from 'shared/utils/featuresUtils';
import RoleSelector from './roleSelector';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '4rem',
    '& button:first-child': {
      marginRight: '1rem',
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '2rem',
    },
  },
  container: {
    [theme.breakpoints.down('sm')]: {
      padding: '1.25rem',
    },
  },
  dialogContainer: {
    inset: '-1.5rem',
    '& .MuiDialog-paperWidthMd': {
      maxWidth: '43rem',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '37.5rem',
    },
  },
  title: {
    marginBottom: '2rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
    },
  },
  description: {
    marginBottom: '2rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  field: {
    marginRight: '.2rem',
    marginBottom: '3.1rem',
    paddingRight: '1.2rem',
    width: '100%',
  },
  inviteDialogContainer: {
    [theme.breakpoints.down('sm')]: {
      padding: '1.25rem',
    },
  },
  roleContainer: {
    marginTop: '1rem',
  },
  closeButton: {
    position: 'absolute',
    right: '2%',
    top: '2%',
    [theme.breakpoints.down('sm')]: {
      right: '0%',
      top: '0%',
    },
  },
}));

export default function InviteUserDialog({
  isLoading,
  dialog,
  isValid,
  autofillUsers,
  onSubmit,
  onToggleDialog,
  validateSelection,
  onRoleChange,
  onUserChange,
  onKeyUp,
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);
  const enableAutocomplete = isFeatureEnabled(
    globalConfiguration?.resource,
    ToggleableFeature.INVITATIONS_AUTOCOMPLETE,
  );

  return (
    <>
      {isLoading && <Backdrop blocking={true} />}
      {!isLoading && (
        <Dialog data-testid="invite-dialog" open={dialog.open} fullWidth maxWidth="md">
          <DialogTitle>
            <Typography component="h2" variant="h2" className={classes.title} gutterBottom>
              {t('user-management.invite.members.dialog.title')}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {t('user-management.invite.members.dialog.description')}
            <Box display="grid">
              <SelectWithSearch
                customValidation={validateSelection}
                options={autofillUsers}
                multiple={false}
                label="random"
                onKeyUp={onKeyUp}
                enableAutocomplete={enableAutocomplete}
                onChange={onUserChange}
              />
              <div className={classes.roleContainer}>
                <Typography component="h3" variant="h3" gutterBottom>
                  {t('user.roles')}
                </Typography>
                <Divider />
                <RoleSelector onRoleChange={onRoleChange} />
              </div>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button data-testid="cancelInvitation" color="default" onClick={() => onToggleDialog(false)}>
              {t('actions.cancel')}
            </Button>
            <Button data-testid="confirmInvitation" disabled={!isValid} onClick={onSubmit} color="primary">
              {t('actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
